import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
    constants,
    translateTimezone,
    formatDateStringToArray,
    getFormattedEventDate,
    getSingularPluralWordForm,
} from "@qgiv/core-js";
import ClassificationCard from "./ClassificationCard";
import { selectAllClassifications } from "../../../redux/slices/classificationsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";

const ConnectedClassificationCard = ({
    id,
    controlId,
    classificationRef,
    type,
    name,
}) => {
    const {
        ENUMS: { DefaultView },
    } = constants;

    const classifications = useSelector(selectAllClassifications);
    const p2pSettings = useSelector(selectAllP2pSettings);
    const {
        enableIndividualRegistration,
        classificationLimitDisplayThreshold,
        classificationLimitDisplayTeamThreshold,
    } = p2pSettings;

    const classification = classifications.find(
        (_classification) => _classification.id === id,
    );
    const {
        city,
        country,
        description,
        displayRegistrantSpotsLeft = false,
        displayTeamSpotsLeft = false,
        endDate,
        hasRegistrationStartAndEndDates,
        title,
        showMap,
        startDate,
        registrantSpotsLeft,
        registrantSpotsLeftOnAllTeams,
        registrationEndDate,
        teamSpotsLeft,
        timezone,
        street,
        zip,
    } = classification;
    // prevents civil war
    const addressState = classification.state;

    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_CLASSIFICATION_STEP,
            state,
        ),
    );

    const {
        options: {
            maxNewTeamsReachedLabel = "No longer accepting new teams.",
            registrationEndDateLabel = "Registration ends %ClassificationRegistrationEndDate%",
            remainingRegistrantSpotsLabel = "Only %RemainingRegistrantSpots% spot(s) left!",
            remainingNewTeamSpotsLabel = "Only %RemainingTeamSpots% spot(s) left for new teams!",
            onlyIndividualRegistrationAvailableLabel = "All teams full. Register as individual.",
            mapLinkLabel = "See Map",
        },
    } = control;

    const formattedDate =
        startDate && startDate.length > 0 && endDate && endDate.length > 0
            ? getFormattedEventDate({
                  startDate,
                  endDate,
                  timezone,
              })
            : "";

    const formattedRegistrationEndDate =
        formatDateStringToArray(registrationEndDate);

    const formattedRegistrationEndDateLabel = hasRegistrationStartAndEndDates
        ? registrationEndDateLabel.replace(
              "%ClassificationRegistrationEndDate%",
              `${formattedRegistrationEndDate.join(" ")} ${translateTimezone(
                  timezone,
              )}`,
          )
        : "";

    const formattedRegistrantLimitLabel = remainingRegistrantSpotsLabel.replace(
        "%RemainingRegistrantSpots%",
        registrantSpotsLeft,
    );

    const formattedTeamLimitLabel = remainingNewTeamSpotsLabel.replace(
        "%RemainingTeamSpots%",
        teamSpotsLeft,
    );

    const formattedTeamLimitReachedLabel = maxNewTeamsReachedLabel;

    const formattedOnlyIndividualRegistrationAvailableLabel =
        onlyIndividualRegistrationAvailableLabel;

    const formattedAddress =
        street && city && addressState && country && zip
            ? `${street}, ${city}, ${addressState} ${zip}`
            : "";

    const encodedAddress =
        street && city && addressState && country && zip
            ? encodeURI(`${street} ${city} ${addressState} ${country} ${zip}`)
            : "";

    const displayAddress = formattedAddress.length > 0;

    const displayMapLink = showMap;

    const displayRegistrantLimit =
        displayRegistrantSpotsLeft &&
        typeof registrantSpotsLeft === "number" &&
        typeof classificationLimitDisplayThreshold === "number" &&
        registrantSpotsLeft <= classificationLimitDisplayThreshold;

    const displayTeamLimit =
        displayTeamSpotsLeft &&
        typeof teamSpotsLeft === "number" &&
        teamSpotsLeft > 0 &&
        typeof classificationLimitDisplayTeamThreshold === "number" &&
        teamSpotsLeft <= classificationLimitDisplayTeamThreshold;

    const displayOnlyIndividualRegistrationAvailable =
        enableIndividualRegistration &&
        typeof registrantSpotsLeftOnAllTeams === "number" &&
        registrantSpotsLeftOnAllTeams === 0;

    const displayTeamLimitReached =
        !displayOnlyIndividualRegistrationAvailable &&
        displayTeamSpotsLeft &&
        typeof teamSpotsLeft === "number" &&
        teamSpotsLeft === 0;

    const displayDate = formattedDate.length > 0;

    const displayEndDate =
        hasRegistrationStartAndEndDates &&
        formattedRegistrationEndDate.length > 0;

    const displayDivider =
        displayAddress ||
        displayDate ||
        displayEndDate ||
        displayRegistrantLimit ||
        displayTeamLimit;

    const classificationCardProps = {
        description,
        displayAddress,
        displayDate,
        displayDivider,
        displayEndDate,
        displayMapLink,
        displayRegistrantLimit,
        displayTeamLimit,
        displayOnlyIndividualRegistrationAvailable,
        displayTeamLimitReached,
        encodedAddress,
        formattedAddress,
        formattedDate,
        formattedOnlyIndividualRegistrationAvailableLabel,
        formattedRegistrantLimitLabel: getSingularPluralWordForm(
            formattedRegistrantLimitLabel,
            registrantSpotsLeft,
        ),
        formattedTeamLimitLabel: getSingularPluralWordForm(
            formattedTeamLimitLabel,
            teamSpotsLeft,
        ),
        formattedTeamLimitReachedLabel,
        id,
        formattedRegistrationEndDateLabel,
        mapLinkLabel,
        title,
        classificationRef,
        type,
        name,
    };

    return <ClassificationCard {...classificationCardProps} />;
};

ConnectedClassificationCard.propTypes = {
    classificationRef: PropTypes.shape({}).isRequired,
    controlId: PropTypes.number.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.oneOf(["radio", "checkbox"]).isRequired,
    name: PropTypes.string.isRequired,
};

export default ConnectedClassificationCard;
